/* eslint-disable @typescript-eslint/no-use-before-define */
import { Typography } from '@euler/app/components/typography';
import { AlphabetIndex } from '@euler/app/flows/order/components/AlphabetIndex';
import { EmptyView } from '@euler/components/EmptyView';
import { Input } from '@euler/components/form/Input';
import {
  getDefectiveLevel,
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
  getDefectiveLevelText,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { DefectiveLevel } from '@euler/model';
import { CustomIssueNameStat } from '@euler/model/CustomIssueNameStat';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { getPyInitials } from '@euler/utils/pinyin/getPyInitials';
import { BottomSheetScrollView, useBottomSheet } from '@gorhom/bottom-sheet';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { FlashList } from '@shopify/flash-list';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { useTheme } from 'styled-components';
import { useSiteInspectionNavigatorContext } from './SiteInspectionContext';
import { SiteInspectionNavParams } from './SiteInspectionNavigator';

type Props = {
  onSelect?: (stat: CustomIssueNameStat) => void;
};

type SortMode = 'freq' | 'py';

type IndexedStat = CustomIssueNameStat & {
  /** Lowercased siteName, precomputed for substring matching. */
  nameLower: string;
  /** Lowercased pinyin initials of siteName. Precomputed to keep keystroke
   * filtering off the hot path (getPyInitials walks the dict). */
  pyInitials: string;
  /** Synthesized defective level. Precomputed so the level filter
   * doesn't recompute it per render. */
  defectiveLevel: DefectiveLevel;
};

const LevelFilters: Array<{ value: DefectiveLevel | null; label: string }> = [
  { value: null, label: '全部' },
  {
    value: DefectiveLevel.Urgent,
    label: getDefectiveLevelText(DefectiveLevel.Urgent),
  },
  {
    value: DefectiveLevel.Warning,
    label: getDefectiveLevelText(DefectiveLevel.Warning),
  },
  {
    value: DefectiveLevel.Defective,
    label: getDefectiveLevelText(DefectiveLevel.Defective),
  },
];

export const SelectCustomIssueScreen = wrapNavigatorScreen(
  ({ onSelect }: Props) => {
    const { rpx } = useResponsiveContext();
    const { storeService } = useServiceFactory();
    const navigation =
      useNavigation<StackNavigationProp<SiteInspectionNavParams>>();
    const safeAreaInsets = useSafeAreaInsets();
    const [stats, setStats] = useState<IndexedStat[]>([]);
    const [loading, setLoading] = useState(true);
    const [sortMode, setSortMode] = useState<SortMode>('freq');
    const [levelFilter, setLevelFilter] = useState<DefectiveLevel | null>(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // Single source of truth for the search input. The BehaviorSubject's
    // current value drives the controlled <Input/> (via useBehaviorSubject),
    // and a parallel pipe debounces the same stream by 500ms to feed the
    // filter — mirrors the useSearchResults rxjs pattern.
    const keyword$ = useCreation(() => new BehaviorSubject<string>(''), []);
    const [keyword, setKeyword] = useBehaviorSubject(keyword$);
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    useEffect(() => {
      const sub = keyword$
        .pipe(debounceTime(500))
        .subscribe(setDebouncedKeyword);
      return () => sub.unsubscribe();
    }, [keyword$]);

    // The parent CustomIssueScreen pins a "完成" button via this context.
    // Hide it while the select screen is in focus so the list isn't covered
    // and an unrelated submit can't fire from this screen.
    const { bottomComponent$, navigatorContainerType } =
      useSiteInspectionNavigatorContext();
    // BottomSheetFlatList is required when nested in the bottom-sheet host so
    // pan gestures cooperate with the sheet's internal gesture handler. In
    // other surfaces (side panel, web) FlashList wins on perf.
    const useBottomSheetList =
      navigatorContainerType === 'bottomsheet' && Platform.OS !== 'web';
    useFocusEffect(
      useCallback(() => {
        const previous = bottomComponent$.value;
        bottomComponent$.next(undefined);
        return () => {
          bottomComponent$.next(previous);
        };
      }, [bottomComponent$]),
    );

    useEffect(() => {
      let canceled = false;
      setLoading(true);
      storeService
        .getCustomIssueNameStats()
        .then(items => {
          if (canceled) return;
          const indexed: IndexedStat[] = items.map(item => ({
            ...item,
            nameLower: item.siteName.toLowerCase(),
            pyInitials: getPyInitials(item.siteName).toLowerCase(),
            defectiveLevel: getDefectiveLevel(
              item.abnormalLevel,
              item.severityLevel,
            ),
          }));
          setStats(indexed);
        })
        .catch(onErrorIgnore)
        .finally(() => {
          if (!canceled) setLoading(false);
        });
      return () => {
        canceled = true;
      };
    }, [storeService]);

    const sorted = useMemo(() => {
      const copy = [...stats];
      if (sortMode === 'py') {
        copy.sort((a, b) => a.pyInitials.localeCompare(b.pyInitials));
      } else {
        copy.sort((a, b) => b.count - a.count);
      }
      return copy;
    }, [stats, sortMode]);

    // Imperative scroll handle for the alphabet index. Typed loosely so both
    // BottomSheetFlatList (FlatList instance) and FlashList instance can share
    // the same ref; both expose `scrollToOffset({offset, animated})`.
    // We prefer scrollToOffset over scrollToIndex because index-based scrolling
    // on virtualized lists can throw natively for rows that haven't been
    // measured yet — easy to trigger while dragging across the alphabet bar.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listRef = useRef<any>(null);
    // Row content (~78rpx) + outer vertical margin (4rpx top + 4rpx bottom).
    // Margin must be included so scrollToOffset lands at the right row and so
    // FlashList sizes recycled cells correctly.
    const estimatedRowHeight = rpx(78 + 4 * 2);

    const filtered = useMemo(() => {
      const q = debouncedKeyword.trim().toLowerCase();
      return sorted.filter(stat => {
        if (levelFilter != null && stat.defectiveLevel !== levelFilter) {
          return false;
        }
        if (!q) return true;
        return stat.nameLower.includes(q) || stat.pyInitials.includes(q);
      });
    }, [debouncedKeyword, sorted, levelFilter]);

    // When sorted by pinyin, surface an A-Z side index for quick navigation.
    // Build it from the visible (filtered) rows so jumping always lands on a
    // row that's currently in view.
    const alphabetIndex = useMemo(() => {
      if (sortMode !== 'py') return null;
      const firstRowByLetter = new Map<string, number>();
      filtered.forEach((stat, idx) => {
        const letter = stat.pyInitials.charAt(0).toUpperCase();
        if (letter && !firstRowByLetter.has(letter)) {
          firstRowByLetter.set(letter, idx);
        }
      });
      const letters = [...firstRowByLetter.keys()].sort();
      return { letters, firstRowByLetter };
    }, [filtered, sortMode]);

    // While the bottom sheet is at a partial snap point, BottomSheetFlatList
    // pins its inner scroll at 0 (touches drag the sheet rather than the
    // list). Expand the sheet first so the programmatic scroll lands.
    const expandSheetRef = useRef<() => void>(() => undefined);
    const setExpandSheet = useCallback((fn: () => void) => {
      expandSheetRef.current = fn;
    }, []);

    const onAlphabetIndexChange = useCallback(
      (i: number) => {
        if (!alphabetIndex) return;
        const letter = alphabetIndex.letters[i];
        if (letter == null) return;
        const rowIndex = alphabetIndex.firstRowByLetter.get(letter);
        if (rowIndex == null) return;
        expandSheetRef.current();
        // Use scrollToIndex (FlashList resolves the real cell position) rather
        // than scrollToOffset × estimate — rows have variable height when the
        // maintenance-advice line is present, and the estimate-based offset
        // would land off-target.
        listRef.current?.scrollToIndex({
          index: rowIndex,
          animated: false,
          viewPosition: 0,
        });
      },
      [alphabetIndex],
    );

    const handleRowPress = useCallback(
      (item: IndexedStat) => {
        onSelect?.(item);
        navigation.goBack();
      },
      [navigation, onSelect],
    );

    const renderRow = useCallback(
      ({ item }: { item: IndexedStat }) => (
        <CustomIssueRow stat={item} onPress={handleRowPress} />
      ),
      [handleRowPress],
    );

    return (
      <View
        css={`
          flex: 1;
          background-color: #f5f5f5;
        `}
      >
        <View
          onLayout={e => setHeaderHeight(e.nativeEvent.layout.height)}
          css={`
            padding: ${rpx(10)}px ${rpx(20)}px;
            background-color: #fff;
            border-bottom-width: 1px;
            border-bottom-color: #ececec;
          `}
        >
          <Input
            value={keyword}
            placeholder="搜索部件名称或拼音首字母"
            onChangeText={setKeyword}
            autoCapitalize="none"
            css={`
              background-color: #f3f4f6;
              border-width: 0;
              border-radius: ${rpx(8)}px;
              padding: ${rpx(8)}px ${rpx(12)}px;
              font-size: ${rpx(15)}px;
            `}
          />
          <View
            css={`
              flex-direction: row;
              align-items: center;
              margin-top: ${rpx(10)}px;
            `}
          >
            <Typography.Label
              size={rpx(12)}
              color="#888"
              css={`
                margin-right: ${rpx(8)}px;
              `}
            >
              排序
            </Typography.Label>
            <SortPill
              label="按频次"
              active={sortMode === 'freq'}
              onPress={() => setSortMode('freq')}
            />
            <SortPill
              label="按拼音"
              active={sortMode === 'py'}
              onPress={() => setSortMode('py')}
            />
          </View>
          <View
            css={`
              flex-direction: row;
              align-items: center;
              flex-wrap: wrap;
              margin-top: ${rpx(8)}px;
            `}
          >
            <Typography.Label
              size={rpx(12)}
              color="#888"
              css={`
                margin-right: ${rpx(8)}px;
              `}
            >
              筛选
            </Typography.Label>
            {LevelFilters.map(item => (
              <LevelFilterChip
                key={item.value ?? 'all'}
                label={item.label}
                level={item.value}
                active={levelFilter === item.value}
                onPress={() => setLevelFilter(item.value)}
              />
            ))}
          </View>
        </View>

        {loading && stats.length === 0 ? (
          <View
            css={`
              flex: 1;
              align-items: center;
              justify-content: center;
            `}
          >
            <ActivityIndicator />
          </View>
        ) : (
          <FlashList
            ref={listRef}
            data={filtered}
            keyExtractor={(item, idx) => `${item.siteName}-${idx}`}
            renderItem={renderRow}
            estimatedItemSize={estimatedRowHeight}
            // Swap FlashList's internal scroller for BottomSheetScrollView when
            // hosted inside @gorhom/bottom-sheet — this is the integration the
            // FlashList docs recommend and it brings back native iOS scroll
            // momentum + proper gesture cooperation with the sheet.
            renderScrollComponent={
              useBottomSheetList
                ? // The type signatures of FlashList's renderScrollComponent
                  // and BottomSheetScrollView don't line up (different
                  // children typing), but the runtime contract matches.
                  (BottomSheetScrollView as never)
                : undefined
            }
            ListEmptyComponent={
              <EmptyView
                message={
                  debouncedKeyword ? '未匹配到自定义部件' : '暂无历史自定义部件'
                }
              />
            }
            contentContainerStyle={{
              paddingTop: rpx(8),
              paddingBottom: rpx(20) + safeAreaInsets.bottom,
            }}
            keyboardShouldPersistTaps="handled"
          />
        )}
        {sortMode === 'py' &&
        alphabetIndex &&
        alphabetIndex.letters.length > 0 ? (
          <AlphabetIndex
            indics={alphabetIndex.letters}
            onChange={onAlphabetIndexChange}
            top={headerHeight + rpx(24)}
            right={-rpx(14)}
          />
        ) : null}
        {useBottomSheetList ? (
          <ExpandSheetBridge onReady={setExpandSheet} />
        ) : null}
      </View>
    );
  },
  { title: '选择自定义部件' },
);

/**
 * Tiny bridge component mounted only when nested in a BottomSheet host so the
 * `useBottomSheet` hook context exists. Hands an `expand()` callback back to
 * the parent via the onReady prop.
 */
const ExpandSheetBridge = memo(
  ({ onReady }: { onReady: (expand: () => void) => void }) => {
    const sheet = useBottomSheet();
    useEffect(() => {
      onReady(() => sheet.expand());
    }, [sheet, onReady]);
    return null;
  },
);

const InlineLabeledText = memo(
  ({ label, value }: { label: string; value: string }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          flex: 1;
          flex-direction: row;
          align-items: center;
          border-width: 1px;
          border-color: #e5e7eb;
          border-radius: ${rpx(6)}px;
          padding: ${rpx(4)}px ${rpx(8)}px;
          background-color: #fafafa;
        `}
      >
        <Typography.Label
          size={rpx(11)}
          color="#9ca3af"
          css={`
            margin-right: ${rpx(6)}px;
          `}
        >
          {label}
        </Typography.Label>
        <Typography.Label
          medium
          size={rpx(12)}
          color="#374151"
          numberOfLines={1}
          css={`
            flex: 1;
          `}
        >
          {value}
        </Typography.Label>
      </View>
    );
  },
);

const DefectiveLevelPill = memo(({ level }: { level: DefectiveLevel }) => {
  const { rpx } = useResponsiveContext();
  const theme = useTheme();
  const textColor = getDefectiveLevelColor(theme, level);
  const bgColor = getDefectiveLevelBgColor(theme, level);
  return (
    <View
      css={`
        background-color: ${bgColor};
        border-radius: ${rpx(3)}px;
        padding: ${rpx(1)}px ${rpx(6)}px;
      `}
    >
      <Typography.Label
        medium
        size={rpx(10)}
        lineHeight={rpx(14)}
        color={textColor}
      >
        {getDefectiveLevelText(level)}
      </Typography.Label>
    </View>
  );
});

const LevelFilterChip = memo(
  ({
    label,
    level,
    active,
    onPress,
  }: {
    label: string;
    level: DefectiveLevel | null;
    active: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    const accent =
      level != null ? getDefectiveLevelColor(theme, level) : '#4b5563';
    const accentBg =
      level != null ? getDefectiveLevelBgColor(theme, level) : '#f3f4f6';
    return (
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        css={`
          background-color: ${active ? accent : accentBg};
          border-radius: ${rpx(12)}px;
          padding: ${rpx(3)}px ${rpx(10)}px;
          margin-right: ${rpx(6)}px;
          margin-bottom: ${rpx(4)}px;
        `}
      >
        <Typography.Label
          medium
          size={rpx(11)}
          color={active ? '#ffffff' : accent}
        >
          {label}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);

const SortPill = memo(
  ({
    label,
    active,
    onPress,
  }: {
    label: string;
    active: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        css={`
          background-color: ${active ? '#2563eb' : '#f3f4f6'};
          border-radius: ${rpx(12)}px;
          padding: ${rpx(3)}px ${rpx(10)}px;
          margin-right: ${rpx(6)}px;
        `}
      >
        <Typography.Label
          medium
          size={rpx(11)}
          color={active ? '#ffffff' : '#4b5563'}
        >
          {label}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);

const CustomIssueRow = memo(
  ({
    stat,
    onPress,
  }: {
    stat: IndexedStat;
    /** Pure callback — receives the row's stat so the parent can pass a
     * stable function (no per-row closure), keeping memoization effective. */
    onPress: (stat: IndexedStat) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    const level = stat.defectiveLevel;
    const levelColor = getDefectiveLevelColor(theme, level);
    const handlePress = useCallback(() => onPress(stat), [onPress, stat]);
    return (
      <TouchableOpacity
        onPress={handlePress}
        activeOpacity={0.6}
        css={`
          background-color: #fff;
          margin: ${rpx(4)}px ${rpx(20)}px;
          border-radius: ${rpx(8)}px;
          padding: ${rpx(10)}px ${rpx(12)}px;
          overflow: hidden;
          position: relative;
        `}
      >
        <View
          css={`
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: ${rpx(4)}px;
            background-color: ${levelColor};
          `}
        />
        <View
          css={`
            flex-direction: row;
            align-items: center;
          `}
        >
          <Typography.Label
            medium
            size={rpx(15)}
            color="#111"
            numberOfLines={1}
            css={`
              flex: 1;
              margin-right: ${rpx(8)}px;
            `}
          >
            {stat.siteName}
          </Typography.Label>
          <DefectiveLevelPill level={level} />
        </View>
        {stat.inspectionResult || stat.topMaintenanceAdvice ? (
          <View
            css={`
              flex-direction: row;
              align-items: center;
              margin-top: ${rpx(6)}px;
            `}
          >
            {stat.inspectionResult ? (
              <InlineLabeledText
                label="故障形式"
                value={stat.inspectionResult}
              />
            ) : null}
            {stat.inspectionResult && stat.topMaintenanceAdvice ? (
              <View
                css={`
                  width: ${rpx(8)}px;
                `}
              />
            ) : null}
            {stat.topMaintenanceAdvice ? (
              <InlineLabeledText
                label="维修建议"
                value={stat.topMaintenanceAdvice}
              />
            ) : null}
          </View>
        ) : null}
      </TouchableOpacity>
    );
  },
);
